import {ActionIcon, Button, Divider, Group, Menu, Modal, ModalProps, Stack, Title} from "@mantine/core";
import {ServiceTypeFacetDataTable} from "../../data-table/ServiceTypeFacetDataTable/ServiceTypeFacetDataTable";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {
  CompanyEntity,
  getObjectId,
  ServiceTypeEntity,
  serviceTypeFacetsSelector,
  useGetAllServiceTypeFacetsQuery
} from "@ots-frontend/ots-data";
import {Card, CardProps} from "../Card/Card";
import {IconPlus} from "@tabler/icons-react";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {useDisclosure} from "@mantine/hooks";
import {useServiceTypeFacetMutation} from "../../../hooks/useServiceTypeFacetMutation";
import {useForm} from "@mantine/form";
import {RefServiceStatusFacetSelect} from "../../select/entity/RefServiceStatusFacetSelect/RefServiceStatusFacetSelect";
import {RefServiceStatusSelect} from "../../select/entity/RefServiceStatusSelect/RefServiceStatusSelect";

export type ServiceTypeFacetDataTableCardProps = CardProps & {
  company: CompanyEntity | string
  serviceType: ServiceTypeEntity | string
}

export const ServiceTypeFacetDataTableCard = (props: ServiceTypeFacetDataTableCardProps) => {
  const {
    company,
    serviceType,
    shadow = "sm",
    padding = "lg",
    radius = "md",
    withBorder = true,
    ...rest
  } = props

  const {t} = useTranslation(["common"])

  const [modalOpened, modalHandler] = useDisclosure(false);

  const selectServiceTypeFacets = useMemo(() => {
    return serviceTypeFacetsSelector();
  }, []);

  const {
    serviceTypeFacets,
    isServiceTypeFacetsLoading
  } = useGetAllServiceTypeFacetsQuery({
    companyId: getObjectId(company),
    serviceTypeId: getObjectId(serviceType),
  }, {
    selectFromResult: (result) => ({
      serviceTypeFacets: selectServiceTypeFacets(result, {}),
      isServiceTypeFacetsLoading: result.isLoading
    })
  });

  const {
    createServiceTypeFacet,
    isCreating,
  } = useServiceTypeFacetMutation()

  const onNewClick = useCallback(() => {
    modalHandler.open();
  }, [modalHandler])

  const onSubmit = useCallback((value: {
    facet: string,
    resetStatus?: string,
    defaultBaselineStatus?: string
  } | undefined) => {
    if (value) {
      createServiceTypeFacet({
        companyId: getObjectId(company),
        serviceTypeId: getObjectId(serviceType),
        facet: value.facet,
        resetStatus: value.resetStatus,
        defaultBaselineStatus: value.defaultBaselineStatus,
      }, {
        onSuccess: () => modalHandler.close()
      })
    }
  }, [createServiceTypeFacet, company, serviceType, modalHandler])

  return (
    <Card shadow={shadow} padding={padding} radius={radius} withBorder={withBorder} {...rest}>
      <Group position="apart" pb="sm">
        <Title order={4}>{t("common.entity.servicetypefacet.plural", "Service Type Facets")}</Title>
        <Group spacing={4} position="left" noWrap>
          <HasAccess permissions={["servicetype_edit"]}>
            <Menu
              width={260}
              position="bottom-end"
              transitionProps={{transition: "pop-top-right"}}
              withinPortal
            >
              <Menu.Target>
                <ActionIcon color="green">
                  <IconPlus size={16}/>
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={onNewClick}>
                  {t("common.actions.new", "New") + " " + t("common.entity.servicetypefacet", "Service Type Facet")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </HasAccess>
        </Group>
      </Group>

      <Divider pb="sm"/>

      <ServiceTypeFacetDataTable
        withBorder
        records={serviceTypeFacets}
        fetching={isServiceTypeFacetsLoading || isCreating}
      />

      <ServiceTypeFacetModal
        opened={modalOpened}
        onClose={modalHandler.close}
        title={t("common.entity.servicetypefacet.plural", "Service Type Facets")}
        onSubmit={onSubmit}
      />

    </Card>
  )
}

interface ServiceTypeFacetModalProps extends Omit<ModalProps, "onSubmit"> {
  onSubmit?: (value: { facet: string, resetStatus?: string, defaultBaselineStatus?: string } | undefined) => void
}

const ServiceTypeFacetModal = (props: ServiceTypeFacetModalProps) => {
  const {
    onSubmit,
    onClose,
    ...rest
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: {
      facet: undefined,
      resetStatus: undefined,
      defaultBaselineStatus: undefined,
    },
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        facet: values.facet!,
        resetStatus: values.resetStatus,
        defaultBaselineStatus: values.defaultBaselineStatus,
      })
      form.reset()
    }
  }, [form, onSubmit]);

  const _onClose = useCallback(() => {
    onClose?.()
    form.reset()
  }, [onClose, form])

  return (
    <Modal {...rest} onClose={_onClose}>
      <form onSubmit={form.onSubmit(onFormSubmit)}>
        <Stack spacing="md">
          <RefServiceStatusFacetSelect
            label={t("common.entity.servicetypefacet.form.field.facet", "Facet")}
            placeholder={t<string>("common.entity.servicetypefacet.form.field.facet", "Facet")}
            pt="md"
            withinPortal={true}
            clearable
            withAsterisk
            required
            {...form.getInputProps("facet")} />
          <RefServiceStatusSelect
            label={t("common.entity.servicetypefacet.form.field.resetstatus", "Reset Status")}
            placeholder={t<string>("common.entity.servicetypefacet.form.field.resetstatus", "Reset Status")}
            pt="md"
            withinPortal={true}
            clearable
            {...form.getInputProps("resetStatus")} />
          <RefServiceStatusSelect
            label={t("common.entity.servicetypefacet.form.field.defaultbaselinestatus", "Default Baseline Status")}
            placeholder={t<string>("common.entity.servicetypefacet.form.field.defaultbaselinestatus", "Default Baseline Status")}
            pt="md"
            withinPortal={true}
            clearable
            {...form.getInputProps("defaultBaselineStatus")} />
          <Button type="submit" fullWidth mt="xl">
            {t("common.action.submit", {ns: "common"})}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
};
